import i18n from 'i18next';
import { functionKeys } from 'config/functionKeysAndDisplayConfig';
import {
    lotteryInstantTypes,
    lotteryParticipantTypes,
    lotteryAdminTypes
} from 'config/promoLotteryTypes';
import { fromEntries } from 'utils/core';

export const lotteryCategories = {
    IS_INSTANT_LOTTERY: 'isLotteryInstant',
    IS_PARTICIPANT_LOTTERY: 'isLotteryParticipant',
    IS_ADMIN_LOTTERY: 'isLotteryAdmin'
};

export const prizeQuantityTypes = {
    LIMITED: 1,
    UNLIMITED: 0
};

const adminPrize = {
    label: () => i18n.t('label.lottery'),
    value: lotteryAdminTypes.ADMIN,
    category: lotteryCategories.IS_ADMIN_LOTTERY,
    settingKey: functionKeys.lotteries.ADMIN
};
const participantPrize = {
    label: () => i18n.t('label.instant_win'),
    value: lotteryInstantTypes.JOIN_AND_GET,
    category: lotteryCategories.IS_INSTANT_LOTTERY,
    settingKey: functionKeys.lotteries.JOIN_AND_GET
};
const sketchPrize = {
    label: () => i18n.t('label.sketch_prize'),
    value: lotteryParticipantTypes.SCRATCH_CARD,
    category: lotteryCategories.IS_PARTICIPANT_LOTTERY,
    isSketch: true,
    additionalConfig: 'scratch_card',
    settingKey: functionKeys.lotteries.SCRATCH_CARD
};
const winWheelPrize = {
    label: () => i18n.t('label.winwheel'),
    value: lotteryParticipantTypes.WIN_WHEEL,
    category: lotteryCategories.IS_PARTICIPANT_LOTTERY,
    additionalConfig: 'wheel',
    settingKey: functionKeys.lotteries.WIN_WHEEL
};
const pokeGamePrize = {
    label: () => i18n.t('label.poke_game'),
    value: lotteryParticipantTypes.POKE_GAME,
    category: lotteryCategories.IS_PARTICIPANT_LOTTERY,
    additionalConfig: 'poke_game',
    settingKey: functionKeys.lotteries.POKE_GAME
};
const redEnvelopeRainPrize = {
    label: () => i18n.t('label.red_envelope_rain'),
    value: lotteryParticipantTypes.RED_ENVELOPE_RAIN,
    category: lotteryCategories.IS_PARTICIPANT_LOTTERY,
    additionalConfig: 'red_envelope_rain',
    settingKey: functionKeys.lotteries.RED_ENVELOPE_RAIN
};
const slotsPrize = {
    label: () => i18n.t('label.slots'),
    value: lotteryParticipantTypes.SLOTS,
    category: lotteryCategories.IS_PARTICIPANT_LOTTERY,
    additionalConfig: 'slots',
    settingKey: functionKeys.lotteries.SLOTS
};
const pickThePrizePrize = {
    label: () => i18n.t('label.game_type_pick_the_prize'),
    value: lotteryParticipantTypes.PICK_THE_PRIZE,
    category: lotteryCategories.IS_PARTICIPANT_LOTTERY,
    additionalConfig: 'pickThePrize',
    settingKey: functionKeys.lotteries.PICK_THE_PRIZE
};
const pointsRedeemPrize = {
    label: () => i18n.t('label.game_type_points_redeem'),
    value: lotteryParticipantTypes.POINTS_REDEEM,
    category: lotteryCategories.IS_PARTICIPANT_LOTTERY,
    additionalConfig: 'pointsRedeem',
    settingKey: functionKeys.lotteries.POINTS_REDEEM
};

export const lotteryConfigList = [
    adminPrize,
    participantPrize,
    sketchPrize,
    winWheelPrize,
    pokeGamePrize,
    redEnvelopeRainPrize,
    slotsPrize,
    pickThePrizePrize,
    pointsRedeemPrize
];

export const defaultSelectableLotteryTypesList = Object.values(lotteryCategories);
export function getLotteryConfig(type) {
    return (
        lotteryConfigList.find(({ value }) => Number(type) === value) || {
            label: () => {}
        }
    );
}

export function getUnCreatedLotteryCategories(lotteryList) {
    const enableCreateLotteriesOptions = defaultSelectableLotteryTypesList.filter(
        category => !lotteryList.map(lottery => lottery.category).includes(category)
    );
    return enableCreateLotteriesOptions;
}

export function getCreatedLotteryCategories(lotteryList) {
    const enableCreateLotteriesOptions = defaultSelectableLotteryTypesList.filter(category =>
        lotteryList.map(lottery => lottery.category).includes(category)
    );
    return enableCreateLotteriesOptions;
}

export function getDefaultLotteryTypeByCategory(lotteryCategory) {
    const lotteryType = (category => {
        switch (category) {
            case lotteryCategories.IS_ADMIN_LOTTERY:
                return ADMIN_PRIZE_DEFAULT_VALUE;
            case lotteryCategories.IS_INSTANT_LOTTERY:
                return INSTANT_PRIZE_DEFAULT_VALUE;
            case lotteryCategories.IS_PARTICIPANT_LOTTERY:
            default:
                return PARTICIPANT_PRIZE_DEFAULT_VALUE;
        }
    })(lotteryCategory);
    return lotteryType;
}

export function getActivePrizeTabByCategory(lotteryCategory) {
    const lotteryType = (category => {
        switch (category) {
            case lotteryCategories.IS_ADMIN_LOTTERY:
                return prizeTabPanelIds.lotteryPrizeManageTab;
            case lotteryCategories.IS_INSTANT_LOTTERY:
                return prizeTabPanelIds.instantPrizeManageTab;
            case lotteryCategories.IS_PARTICIPANT_LOTTERY:
            default:
                return prizeTabPanelIds.participantPrizeManageTab;
        }
    })(lotteryCategory);
    return lotteryType;
}

export function getShouldExpandTabIndex(lotteryCategory) {
    const tabIndex = defaultSelectableLotteryTypesList.findIndex(c => c === lotteryCategory);
    return tabIndex;
}

export const getDefaultTab = lotteryCategory => getActivePrizeTabByCategory(lotteryCategory);

// 檢查目前 Share LOTTERY 跟 primary LOTTERY 有無共用獎池， 代表會顯示 共用獎項 Chip
export const checkCurrentLotteryIsBindingPrimaryLottery = (
    lotteryCategory,
    isPrimaryLotteryInstantSharing,
    isPrimaryLotteryParticipantSharing
) => {
    if (lotteryCategory === lotteryCategories.IS_INSTANT_LOTTERY) {
        return isPrimaryLotteryInstantSharing;
    }

    if (lotteryCategory === lotteryCategories.IS_PARTICIPANT_LOTTERY) {
        return isPrimaryLotteryParticipantSharing;
    }

    return false;
};

// 抽獎不會有共用獎池設定，故不加上
export const getExistEnableBindingPrimaryLottery = (
    primaryLotteryInstant = {},
    primaryLotteryParticipant = {}
) =>
    [primaryLotteryInstant, primaryLotteryParticipant].filter(
        lottery => Object.keys(lottery).length > 0
    );

// 所有配獎動畫的 tab panel, 要對應 prizeType 才可以在不同配獎模式的動畫編輯處正常切換，不然會一直出現上一個編輯動畫的 panel
export const gameDesignTabPanels = {
    participantPrizeDesignScratchCardTab: {
        id: 'participantPrize_design_scratch_card',
        label: 'label.scratch_card_design',
        lotteryType: sketchPrize.value
    },
    participantPrizeDesignWinWheelTab: {
        id: 'participantPrize_design_Winwheel',
        label: 'label.win_wheel_design',
        lotteryType: winWheelPrize.value
    },
    participantPrizeDesignPokeGameTab: {
        id: 'participantPrize_design_poke_game',
        label: 'label.poke_game_design',
        lotteryType: pokeGamePrize.value
    },
    participantPrizeDesignRedEnvelopeRainTab: {
        id: 'participantPrize_design_red_envelope_rain',
        label: 'label.red_envelope_rain_design',
        lotteryType: redEnvelopeRainPrize.value
    },
    participantPrizeDesignSlotsTab: {
        id: 'participantPrize_design_slots',
        label: 'label.slots_design',
        lotteryType: slotsPrize.value
    },
    participantPrizeDesignPickThePrizeTab: {
        id: 'participantPrize_design_pick_the_prize',
        label: 'label.pick_the_prize_design',
        lotteryType: pickThePrizePrize.value
    },
    participantPrizeDesignPointsRedeemTab: {
        id: 'participantPrize_design_points_redeem',
        label: 'label.points_redeem_design',
        lotteryType: pointsRedeemPrize.value
    }
};

export function isGameDesignLotteryType(type) {
    return Object.entries(gameDesignTabPanels).some(([, tab]) => tab.lotteryType === type);
}

// 各個對應的 tab panel id
export const prizeTabPanelIds = {
    participantPrizeManageTab: 'participantPrize_manage',
    participantPrizeRuleTab: 'participantPrize_rule',
    lotteryPrizeManageTab: 'lotteryPrize_manage',
    lotteryPrizeRuleTab: 'lotteryPrize_rule',
    instantPrizeManageTab: 'instantPrize_manage',
    instantPrizeRuleTab: 'instantPrize_rule',
    prizeRelatedSettingTab: 'prize_related_setting',
    responseFormTab: 'responseForm',
    lotteryTaskPrizeDesignPickThePrizeTab: 'lotteryTaskPrizeDesignTab',
    ...fromEntries(Object.entries(gameDesignTabPanels).map(([key, tab]) => [key, tab.id]))
};

export const INSTANT_PRIZE_DEFAULT_VALUE = participantPrize.value;
export const ADMIN_PRIZE_DEFAULT_VALUE = adminPrize.value;
export const PARTICIPANT_PRIZE_DEFAULT_VALUE = sketchPrize.value;

export const prizeTabTypes = {
    UGC_TAB_INDEX: 0,
    VOTE_TAB_INDEX: 1,
    PRIZE_TAB_INDEX: 2,
    TASK_TAB_INDEX: 3,
    CHECK_IN_TAB_INDEX: 4,
    SHARE_INVITE_TAB_INDEX: 5,
    SHARE_SPECIFY_TAB_INDEX: 6,
    PRIZE_SETTINGS: 7
};
